import { msg } from "@lingui/core/macro";
import { createMemo, For, Show } from "solid-js";
import { useNavigate, useSearch } from "@tanstack/solid-router";
import { Mail, Pencil, Plus, Trash2 } from "@fleet/icons";
import { Button } from "@/components/ui/button";
import { Badge, Skeleton } from "@fleet/ui";
import { api, createApiMutation, createApiQuery } from "@/lib/api";
import type { components } from "@apunte-digital/shared";
import { useI18n } from "@/providers/i18n-provider";
import { useAdminCapabilities } from "../use-admin-capabilities";
import { EmailTemplateEditor } from "./email-template-editor";
import { QueryErrorState } from "@/components/ui/query-error-state";

type EmailTemplate = components["schemas"]["EmailTemplate"];

/**
 * Email-template CRUD (campaign #11). Lists templates (system +
 * custom), allows creating/editing/deleting. System templates cannot be deleted.
 */
export function EmailTemplateManager() {
  const { t } = useI18n();
  // CREATION is hidden (there is nothing to read in an empty template), and
  // deletion is hidden too; opening an existing one remains available in read mode
  // (the body already travels in the list GET), so "Edit" is untouched.
  const { can } = useAdminCapabilities();
  const canWrite = () => can("content.write");

  // Deep-linkable editor: `/admin/email?new=1` (create) or `?edit=<id>` (edit the
  // specific template). The URL is the source of truth — pasting the link reopens
  // the exact editor. The template being edited is resolved by id against the
  // already-loaded list (idempotent; if the id does not exist, the editor simply does not open).
  const urlSearch = useSearch({ strict: false }) as () => { new?: boolean; edit?: string };
  const navigate = useNavigate();

  const templatesQuery = createApiQuery<EmailTemplate[], components["schemas"]["ErrorModel"]>(
    () => ["admin", "email", "templates"],
    async () => api.GET("/api/admin/email/templates"),
    () => ({ staleTime: 30_000 }),
  );

  const creating = () => urlSearch().new === true;
  const editing = createMemo<EmailTemplate | null>(() => {
    const id = urlSearch().edit;
    if (!id) {
      return null;
    }
    return (templatesQuery.data ?? []).find((tpl) => tpl.id === id) ?? null;
  });

  const openCreate = () => {
    void navigate({ to: "/admin/email", search: { new: true }, replace: true });
  };
  const openEdit = (tpl: EmailTemplate) => {
    void navigate({ to: "/admin/email", search: { edit: tpl.id }, replace: true });
  };
  const closeEditor = () => {
    void navigate({ to: "/admin/email", search: {}, replace: true });
  };

  const deleteMutation = createApiMutation(async (vars: { id: string }) =>
    api.POST("/api/admin/email/templates/delete", { body: vars }),
  );

  const handleDelete = async (tpl: EmailTemplate) => {
    if (tpl.isSystem) {
      return;
    }
    try {
      await deleteMutation.mutateAsync({ id: tpl.id });
      await templatesQuery.refetch();
    } catch (err) {
      console.error("Error deleting email template:", err);
    }
  };

  const onSaved = async () => {
    closeEditor();
    await templatesQuery.refetch();
  };

  return (
    <div class="space-y-8">
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-3">
          <Mail class="h-8 w-8 text-primary" />
          <h2 class="text-3xl font-bold tracking-tight">{t(msg`Email Templates`)}</h2>
        </div>
        <Show when={canWrite()}>
          <Button class="gap-2" onClick={openCreate}>
            <Plus class="h-4 w-4" />
            {t(msg`New Template`)}
          </Button>
        </Show>
      </div>

      <Show when={creating() || editing()}>
        <EmailTemplateEditor template={editing()} onSaved={onSaved} onCancel={closeEditor} />
      </Show>

      {/*
        P23 / CC-60 · I3: «no hay respuesta» != «no hay nada». En estado de
        ERROR `isLoading`/`isPending` valen FALSE, así que el guard de abajo
        dejaba pasar, `data` no tenía valor y la pantalla pintaba el VACÍO.
        El bloque se ENVUELVE (no se encadena `&& !isError` en el guard: su
        fallback es el esqueleto y quedarían error + esqueleto a la vez).
      */}
      <Show when={templatesQuery.isError}>
        <QueryErrorState
          testId="admin-email-templates-error"
          compact
          onRetry={() => void templatesQuery.refetch()}
        />
      </Show>
      <Show when={!templatesQuery.isError}>
        <Show
          when={!templatesQuery.isLoading}
          fallback={
            <div class="space-y-3">
              <For each={Array.from({ length: 4 }, () => 0)}>
                {() => <Skeleton class="h-20 w-full rounded-xl" />}
              </For>
            </div>
          }
        >
          <div class="space-y-3">
            <For each={templatesQuery.data ?? []}>
              {(tpl) => (
                <div class="flex items-center justify-between rounded-xl border border-border bg-card p-4">
                  <div class="min-w-0">
                    <div class="flex items-center gap-2">
                      <span class="truncate font-semibold">{tpl.name}</span>
                      <Badge variant="secondary">{tpl.category}</Badge>
                      <Show when={tpl.isSystem}>
                        <Badge>{t(msg`System`)}</Badge>
                      </Show>
                      <Show when={!tpl.isActive}>
                        <Badge variant="outline">{t(msg`Inactive`)}</Badge>
                      </Show>
                    </div>
                    <p class="mt-1 truncate text-sm text-muted-foreground">{tpl.subject}</p>
                    <p class="mt-0.5 text-xs text-muted-foreground">
                      {(() => {
                        const { key } = tpl;
                        const version = tpl.currentVersion;
                        return t(msg`Key: ${key} · v${version}`);
                      })()}
                    </p>
                  </div>
                  <div class="flex shrink-0 items-center gap-2">
                    <Button variant="ghost" size="sm" class="gap-1" onClick={() => openEdit(tpl)}>
                      <Pencil class="h-4 w-4" />
                      {canWrite() ? t(msg`Edit`) : t(msg`View`)}
                    </Button>
                    <Show when={canWrite() && !tpl.isSystem}>
                      <Button
                        variant="ghost"
                        size="sm"
                        class="gap-1 text-destructive"
                        disabled={deleteMutation.isPending}
                        onClick={() => handleDelete(tpl)}
                      >
                        <Trash2 class="h-4 w-4" />
                        {t(msg`Delete`)}
                      </Button>
                    </Show>
                  </div>
                </div>
              )}
            </For>
          </div>
        </Show>
      </Show>
    </div>
  );
}
